import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Trash, FloppyDisk, FileText } from "@phosphor-icons/react";
import api from "../lib/api";
import { formatDateNZ } from "../lib/format";

const CATEGORIES = ["TV", "Laptop", "Mobile Phone", "Tablet", "Smartwatch", "Camera", "Gaming", "Headphones", "Appliance", "Coffee Machine", "Kitchen", "Outdoor", "Tools", "Home & Garden", "Toys", "Furniture", "Beauty", "Clothing", "Books", "Other"];
const INSURER_CATEGORIES = ["Electronics", "Appliances", "Jewellery & watches", "Tools & equipment", "Furniture", "Sports & outdoor", "Musical instruments", "Other"];

export default function VaultItemDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [fileUrl, setFileUrl] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vault/${id}`);
      setItem(data);
      setForm({
        retailer: data.retailer || "",
        purchase_date: data.purchase_date || "",
        total: data.total ?? "",
        category: data.category || "Other",
        warranty_months: data.warranty_months || 12,
        notes: data.notes || "",
        serial_number: data.serial_number || "",
        replacement_value: data.replacement_value ?? "",
        insurer_category: data.insurer_category || "",
      });
    } catch (e) {
      toast.error("Receipt not found");
      navigate("/dashboard/vault");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Fetch the receipt file as an authed blob URL so it works for email/password users
  useEffect(() => {
    if (!item) return;
    let revoked = null;
    (async () => {
      try {
        const res = await api.get(`/vault/${item.vault_id}/file`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        revoked = url;
        setFileUrl(url);
      } catch (e) { /* file missing or 401 */ }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [item]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        retailer: form.retailer || null,
        purchase_date: form.purchase_date || null,
        total: form.total === "" ? null : Number(form.total),
        category: form.category,
        warranty_months: Number(form.warranty_months),
        notes: form.notes,
        serial_number: form.serial_number || null,
        replacement_value: form.replacement_value === "" || form.replacement_value === null ? null : Number(form.replacement_value),
        insurer_category: form.insurer_category || null,
      };
      await api.put(`/vault/${id}`, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirm("Delete this receipt and warranty record? This can't be undone.")) return;
    try {
      await api.delete(`/vault/${id}`);
      toast.success("Removed");
      navigate("/dashboard/vault");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f7f8]">
        <Header />
        <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="vault-detail-loading">
          <div className="h-3 w-32 bg-[#e4e4e7] animate-pulse" />
          <div className="h-10 w-2/3 bg-[#e4e4e7] animate-pulse mt-4" />
          <div className="h-72 bg-white border border-[#e4e4e7] mt-10 animate-pulse" />
        </div>
      </div>
    );
  }
  if (!item) return null;
  const isPdf = item.content_type === "application/pdf";
  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <Header />
      <main className="max-w-[1100px] mx-auto px-6 py-12" data-testid="vault-detail-page">
        <LaneNav active="vault" />
        <Link to="/dashboard/vault" className="pg-overline inline-flex items-center gap-2 text-[#52525b] hover:text-black mb-6" data-testid="vault-detail-back">
          <ArrowLeft size={12} /> Back to vault
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Receipt file */}
          <div className="lg:col-span-5">
            <div className="pg-overline mb-3">Receipt file</div>
            <div className="bg-white border border-[#e4e4e7] p-2 aspect-[3/4] flex items-center justify-center overflow-hidden" data-testid="vault-detail-file">
              {!fileUrl ? (
                <div className="text-center p-8">
                  <FileText size={48} weight="duotone" color="#a1a1aa" className="mx-auto" />
                  <p className="text-xs text-[#52525b] mt-3 font-mono-pg">Loading receipt…</p>
                </div>
              ) : isPdf ? (
                <a href={fileUrl} target="_blank" rel="noreferrer" className="text-center p-8">
                  <FileText size={64} weight="duotone" color="#002FA7" className="mx-auto" />
                  <p className="font-bold mt-4">Open PDF receipt</p>
                  <p className="text-xs text-[#52525b] mt-1 font-mono-pg">{item.file_name}</p>
                </a>
              ) : (
                <img
                  src={fileUrl}
                  alt={item.file_name}
                  className="max-h-full max-w-full object-contain"
                  data-testid="vault-detail-image"
                />
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <div className="pg-overline mb-2">Receipt details</div>
              <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">
                {item.retailer || "Unknown retailer"}
              </h1>
              <div className="mt-3 inline-flex items-center gap-2 text-sm font-mono-pg uppercase tracking-widest" style={{ color: item.warranty_days_left == null || item.warranty_days_left < 0 ? "#52525b" : item.warranty_days_left <= 30 ? "#D97706" : "#16A34A" }}>
                <ShieldCheck size={14} weight="fill" />
                {item.warranty_days_left == null
                  ? "Warranty unknown"
                  : item.warranty_days_left < 0
                  ? `Warranty expired ${-item.warranty_days_left}d ago`
                  : `${item.warranty_days_left}d of warranty left · expires ${formatDateNZ(item.warranty_expires_at)}`}
              </div>
            </div>

            <div className="bg-white border border-[#e4e4e7] p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Retailer">
                <input value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} className="vault-input" data-testid="vault-detail-retailer" />
              </Field>
              <Field label="Purchase date">
                <input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="vault-input" data-testid="vault-detail-date" />
              </Field>
              <Field label="Total ($ NZD)">
                <input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} className="vault-input" data-testid="vault-detail-total" />
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="vault-input" data-testid="vault-detail-category">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Warranty (months)">
                <input type="number" min={1} max={120} value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} className="vault-input" data-testid="vault-detail-warranty-months" />
              </Field>
              <Field label="Notes">
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. extended warranty, order #" className="vault-input" data-testid="vault-detail-notes" />
              </Field>
            </div>

            {/* ASSET REGISTER PANEL — turns the vault into an insurance-ready asset schedule.
                All three fields are optional. Most useful for items > $200. */}
            <div className="bg-white border border-[#e4e4e7] p-6" data-testid="vault-detail-asset-register">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="pg-overline">Asset register</div>
                  <h3 className="font-heading font-bold tracking-tight mt-1">For your insurance schedule</h3>
                  <p className="text-xs text-[#52525b] mt-1 leading-relaxed max-w-md">
                    Fill these in for high-value items so they're ready if you ever need to claim on contents insurance or replace a stolen item. Optional.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Serial number">
                  <input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="e.g. SN-7H9KX2L4M" className="vault-input" data-testid="vault-detail-serial" />
                </Field>
                <Field label="Replacement value ($ NZD)">
                  <input type="number" step="0.01" min="0" value={form.replacement_value} onChange={(e) => setForm({ ...form, replacement_value: e.target.value })} placeholder={form.total ? `defaults to ${Number(form.total).toFixed(2)}` : "what it'd cost to replace today"} className="vault-input" data-testid="vault-detail-replacement-value" />
                </Field>
                <Field label="Insurer category">
                  <select value={form.insurer_category} onChange={(e) => setForm({ ...form, insurer_category: e.target.value })} className="vault-input" data-testid="vault-detail-insurer-category">
                    <option value="">Select…</option>
                    {INSURER_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {item.items && item.items.length > 0 && (
              <div className="bg-white border border-[#e4e4e7] p-6">
                <div className="pg-overline mb-3">Items on this receipt</div>
                <ul className="text-sm divide-y divide-[#e4e4e7]" data-testid="vault-detail-items">
                  {item.items.map((it, i) => (
                    <li key={`${it.name || "item"}-${i}`} className="flex justify-between py-2.5">
                      <span>{it.name} {it.qty > 1 && <span className="text-[#52525b]">× {it.qty}</span>}</span>
                      {it.price != null && <span className="font-mono-pg">${Number(it.price).toFixed(2)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warranty claim assistant — always available on a vault
                item, regardless of whether the manufacturer warranty is
                in-window. The CGA 1993 protects you on "reasonable
                durability" well past the manufacturer's stated window,
                and we draft the claim grounded in that. */}
            <WarrantyClaimPanel vaultId={item.vault_id} retailerName={item.retailer || "the retailer"} productName={item.product_name || item.notes || "this item"} purchaseDateIso={item.purchase_date} />

            <div className="flex flex-wrap gap-3 items-center">
              <button onClick={save} disabled={saving} className="bg-[#002FA7] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#00207A] disabled:opacity-60" data-testid="vault-detail-save">
                <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save changes"}
              </button>
              <button onClick={del} className="ml-auto text-xs uppercase font-mono-pg tracking-widest text-[#52525b] hover:text-[#FF3B30] flex items-center gap-1.5" data-testid="vault-detail-delete">
                <Trash size={12} /> Delete receipt
              </button>
            </div>
          </div>
        </div>
      </main>
      <style>{`
        .vault-input{width:100%;padding:10px 12px;border:1px solid #e4e4e7;font-size:14px;background:#fff;}
        .vault-input:focus{outline:none;border-color:#002FA7;}
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="pg-overline">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

/**
 * Always-available warranty-claim drafter for vault items. Calls
 * POST /api/vault/{id}/warranty-claim-email which is CGA-anchored, so
 * it works even when the manufacturer warranty has expired. The user
 * describes the fault, optionally what they've already tried, and gets
 * back a polite, retailer-ready email body + subject. Copy buttons let
 * them lift the text into Gmail/Outlook.
 */
function WarrantyClaimPanel({ vaultId, retailerName, productName, purchaseDateIso }) {
  const [issue, setIssue] = useState("");
  const [tried, setTried] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!purchaseDateIso) {
      toast.error("Add the purchase date in the form above first.");
      return;
    }
    if (issue.trim().length < 10) {
      toast.error("Describe the fault in at least a sentence.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/vault/${vaultId}/warranty-claim-email`, {
        issue_description: issue,
        contact_attempts: tried || null,
      });
      setResult(data);
      toast.success("Warranty claim drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft the claim");
    } finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="bg-white border border-[#e4e4e7] p-6" data-testid="vault-warranty-claim-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#D97706]/10 flex-shrink-0">
          <ShieldCheck size={20} weight="duotone" color="#D97706" />
        </div>
        <div className="flex-1">
          <div className="pg-overline text-[#D97706]">Warranty claim assistant</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-1">Draft a warranty claim, any time</h3>
          <p className="text-sm text-[#52525b] mt-2 leading-relaxed">
            Got a fault on {productName}? PriceGuard drafts a polite, retailer-ready email anchored in the NZ Consumer Guarantees Act 1993 - the law that says products must be of acceptable quality and reasonable durability, even when the manufacturer's warranty has expired. Send to {retailerName}, or any retailer you bought from.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <label className="block">
          <span className="pg-overline">What's wrong?</span>
          <textarea
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            rows={3}
            placeholder="e.g. The screen has developed a vertical line down the middle and won't go away."
            className="vault-input mt-2"
            data-testid="vault-warranty-issue-input"
          />
        </label>
        <label className="block">
          <span className="pg-overline">What have you already tried? (optional)</span>
          <input
            value={tried}
            onChange={(e) => setTried(e.target.value)}
            placeholder="e.g. Factory reset, called retailer support twice"
            className="vault-input mt-2"
            data-testid="vault-warranty-tried-input"
          />
        </label>
        <button
          onClick={generate}
          disabled={busy}
          className="bg-[#D97706] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#B05E04] disabled:opacity-60"
          data-testid="vault-warranty-generate-btn"
        >
          <FileText size={16} weight="bold" /> {busy ? "Drafting with AI…" : "Draft the warranty claim"}
        </button>
      </div>

      {result && (
        <div className="mt-6 border border-[#16A34A] bg-[#F0FDF4] p-5" data-testid="vault-warranty-result">
          <div className="pg-overline text-[#16A34A]">Ready to send</div>
          <div className="mt-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="pg-overline">Subject</span>
              <button onClick={() => copy(result.subject)} className="text-[10px] font-mono-pg uppercase tracking-widest text-[#52525b] hover:text-[#0a0a0a]" data-testid="vault-warranty-copy-subject">Copy</button>
            </div>
            <div className="font-mono-pg text-sm bg-white border border-[#e4e4e7] p-3 break-words">{result.subject}</div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="pg-overline">Email body</span>
              <button onClick={() => copy(result.body)} className="text-[10px] font-mono-pg uppercase tracking-widest text-[#52525b] hover:text-[#0a0a0a]" data-testid="vault-warranty-copy-body">Copy body</button>
            </div>
            <pre className="font-mono-pg text-sm bg-white border border-[#e4e4e7] p-3 whitespace-pre-wrap break-words">{result.body}</pre>
          </div>
          <p className="text-xs text-[#52525b] mt-3 font-mono-pg">
            {result.inside_manufacturer_window ? `Inside the typical ${result.warranty_months}-month manufacturer window · ${result.warranty_days_left} days left.` : `Outside the typical ${result.warranty_months}-month manufacturer window · CGA reasonable-durability is your basis.`}
          </p>
        </div>
      )}
    </div>
  );
}
